import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  inject,
  ChangeDetectorRef
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize, forkJoin, Observable } from 'rxjs';
import Swal from 'sweetalert2';
import {
  ParceiroLembretesService,
  UsuarioParceiro,
  LembreteParceiro,
  TipoLembrete,
  FiltroTipo,
  MedicamentoPayload,
  ConsultaPayload,
  MedicamentoDetalhe,
  ConsultaDetalhe
} from '../../../core/services/parceiro-lembretes.service';
type Formulario = {
  usuarioUuid: string; tipo: TipoLembrete;
  nome: string; dosagem: string; formaFarmaceutica: string;
  vezesPorDia: number; dias: string[]; horarios: string[];
  dataInicio: string; dataTermino: string;
  especialidade: string; nomeMedico: string; dataConsulta: string;
  horarioConsulta: string; local: string; agendadoPor: string; preparacao: string;
};

@Component({
  selector: 'app-parceiro-lembretes', standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './parceiro-lembretes.component.html',
  styleUrl: './parceiro-lembretes.component.css'
})
export class ParceiroLembretesComponent implements OnInit {
  private readonly api = inject(ParceiroLembretesService);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly diasOpcoes = [
    { valor: 'SEGUNDA', rotulo: 'Seg' }, { valor: 'TERCA', rotulo: 'Ter' },
    { valor: 'QUARTA', rotulo: 'Qua' }, { valor: 'QUINTA', rotulo: 'Qui' },
    { valor: 'SEXTA', rotulo: 'Sex' }, { valor: 'SABADO', rotulo: 'Sáb' },
    { valor: 'DOMINGO', rotulo: 'Dom' }
  ];
  usuarios: UsuarioParceiro[] = [];
  lembretes: LembreteParceiro[] = [];
  usuarioFiltro = '';
  tipoFiltro: FiltroTipo = 'TODOS';
  pesquisa = '';
  carregando = false;
  salvando = false;
  modalAberto = false;
  editandoId: number | null = null;
  formulario: Formulario = this.formVazio();

  ngOnInit(): void { this.carregarInicial(); }

  private formVazio(): Formulario {
    return {
      usuarioUuid: '', tipo: 'MEDICAMENTO', nome: '', dosagem: '',
      formaFarmaceutica: 'COMPRIMIDO', vezesPorDia: 1, dias: [], horarios: ['08:00'],
      dataInicio: '', dataTermino: '', especialidade: '', nomeMedico: '',
      dataConsulta: '', horarioConsulta: '', local: '', agendadoPor: '', preparacao: ''
    };
  }

  carregarInicial(): void {
    this.carregando = true;
    this.cdr.detectChanges();

    forkJoin({
      usuarios: this.api.listarUsuarios(),
      lembretes: this.api.listar()
    })
      .pipe(
        finalize(() => {
          this.carregando = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: ({ usuarios, lembretes }) => {
          this.usuarios = usuarios;
          this.lembretes = lembretes;
          this.cdr.detectChanges();
        },
        error: err => {
          this.erro(err, 'Não foi possível carregar os lembretes.');
        }
      });
  }

  filtrar(): void {
    this.carregando = true;
    this.cdr.detectChanges();

    this.api.listar(this.usuarioFiltro, this.tipoFiltro)
      .pipe(
        finalize(() => {
          this.carregando = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: itens => {
          this.lembretes = itens;
          this.cdr.detectChanges();
        },
        error: err => {
          this.erro(err, 'Não foi possível filtrar os lembretes.');
        }
      });
  }

  get visiveis(): LembreteParceiro[] {
    const busca = this.pesquisa.trim().toLocaleLowerCase('pt-BR');
    if (!busca) return this.lembretes;
    return this.lembretes.filter(item =>
      [item.nomeUsuario, item.titulo, item.descricao].some(s => (s || '').toLocaleLowerCase('pt-BR').includes(busca))
    );
  }
  get totalMedicamentos(): number { return this.lembretes.filter(l => l.tipo === 'MEDICAMENTO').length; }
  get totalConsultas(): number { return this.lembretes.filter(l => l.tipo === 'CONSULTA').length; }
  get usuarioSelecionadoAtivo(): boolean {
    return this.usuarios.find(u => u.usuarioUuid === this.formulario.usuarioUuid)?.ativo !== false;
  }

  novo(tipo: TipoLembrete = 'MEDICAMENTO'): void {
    this.editandoId = null;
    this.formulario = this.formVazio();
    this.formulario.tipo = tipo;

    if (this.usuarioFiltro) {
      this.formulario.usuarioUuid = this.usuarioFiltro;
    }

    this.modalAberto = true;
    this.cdr.detectChanges();
  }

  fechar(): void {
    if (this.salvando) return;

    this.modalAberto = false;
    this.editandoId = null;
    this.cdr.detectChanges();
  }
  mudarTipo(tipo: TipoLembrete): void { if (this.editandoId === null) this.formulario.tipo = tipo; }
  alternarDia(dia: string): void {
    this.formulario.dias = this.formulario.dias.includes(dia)
      ? this.formulario.dias.filter(d => d !== dia)
      : [...this.formulario.dias, dia];
  }
  ajustarHorarios(): void {
    const quantidade = Math.max(1, Math.min(24, Number(this.formulario.vezesPorDia) || 1));
    this.formulario.vezesPorDia = quantidade;
    this.formulario.horarios = Array.from({ length: quantidade }, (_, i) => this.formulario.horarios[i] ?? '08:00');
  }

  editar(item: LembreteParceiro): void {
    this.carregando = true;
    this.cdr.detectChanges();

    const request: Observable<MedicamentoDetalhe | ConsultaDetalhe> =
      item.tipo === 'MEDICAMENTO'
        ? this.api.buscarMedicamento(item.id)
        : this.api.buscarConsulta(item.id);

    request
      .pipe(
        finalize(() => {
          this.carregando = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: detalhe => {
          this.formulario = this.formVazio();

          this.formulario.tipo = item.tipo;
          this.formulario.usuarioUuid = detalhe.usuarioUuid;
          this.formulario.preparacao = detalhe.preparacao || '';

          if ('vezesPorDia' in detalhe) {
            this.formulario.nome = detalhe.nome;
            this.formulario.dosagem = detalhe.dosagem;
            this.formulario.formaFarmaceutica = detalhe.formaFarmaceutica;
            this.formulario.vezesPorDia = detalhe.vezesPorDia;

            this.formulario.dias = (detalhe.diasSemana || '')
              .split(',')
              .map(d => d.trim())
              .filter(Boolean);

            this.formulario.horarios = (detalhe.horarios || '')
              .split(',')
              .map(h => h.trim())
              .filter(Boolean);

            this.formulario.dataInicio = detalhe.dataInicio;
            this.formulario.dataTermino = detalhe.dataTermino;

          } else {
            this.formulario.especialidade = detalhe.especialidade;
            this.formulario.nomeMedico = detalhe.nomeMedico;
            this.formulario.dataConsulta = detalhe.dataConsulta;

            this.formulario.horarioConsulta =
              detalhe.horarioConsulta?.slice(0, 5) || '';

            this.formulario.local = detalhe.local;
            this.formulario.agendadoPor = detalhe.agendadoPor;
          }

          this.editandoId = item.id;
          this.modalAberto = true;

          // Atualiza o formulário e abre o modal
          this.cdr.detectChanges();
        },

        error: err => {
          this.erro(err, 'Não foi possível abrir o lembrete.');
        }
      });
  }

  salvar(): void {
    if (this.salvando) return;
    const f = this.formulario;
    if (!f.usuarioUuid) { void Swal.fire('Usuário obrigatório', 'Selecione um usuário.', 'warning'); return; }
    if (!this.usuarioSelecionadoAtivo) { void Swal.fire('Usuário inativo', 'Não é possível alterar lembretes deste usuário.', 'warning'); return; }
    let requisicao: Observable<number | void>;
    if (f.tipo === 'MEDICAMENTO') {
      if (!f.nome.trim() || !f.dosagem.trim() || !f.formaFarmaceutica.trim() || !f.dataInicio || !f.dataTermino || !f.dias.length) {
        void Swal.fire('Campos obrigatórios', 'Preencha os dados do medicamento e selecione os dias.', 'warning'); return;
      }
      if (f.dataTermino < f.dataInicio) { void Swal.fire('Datas inválidas', 'A data final deve ser igual ou posterior à inicial.', 'warning'); return; }
      if (f.horarios.length !== Number(f.vezesPorDia) || f.horarios.some(h => !/^([01]\d|2[0-3]):[0-5]\d$/.test(h))) {
        void Swal.fire('Horários inválidos', 'Informe um horário HH:mm para cada dose.', 'warning'); return;
      }
      const payload: MedicamentoPayload = {
        usuarioUuid: f.usuarioUuid, nome: f.nome.trim(), dosagem: f.dosagem.trim(),
        formaFarmaceutica: f.formaFarmaceutica, vezesPorDia: Number(f.vezesPorDia),
        diasSemana: this.diasOpcoes.filter(d => f.dias.includes(d.valor)).map(d => d.valor).join(', '),
        horarios: f.horarios.join(', '), dataInicio: f.dataInicio, dataTermino: f.dataTermino,
        preparacao: f.preparacao.trim()
      };
      requisicao = this.editandoId !== null
        ? this.api.atualizarMedicamento(this.editandoId, payload)
        : this.api.criarMedicamento(payload);
    } else {
      if (![f.especialidade, f.nomeMedico, f.local, f.agendadoPor, f.dataConsulta, f.horarioConsulta].every(v => v.trim())) {
        void Swal.fire('Campos obrigatórios', 'Preencha todos os dados da consulta.', 'warning'); return;
      }
      const payload: ConsultaPayload = {
        usuarioUuid: f.usuarioUuid, especialidade: f.especialidade.trim(),
        nomeMedico: f.nomeMedico.trim(), dataConsulta: f.dataConsulta,
        horarioConsulta: f.horarioConsulta, local: f.local.trim(),
        agendadoPor: f.agendadoPor.trim(), preparacao: f.preparacao.trim()
      };
      requisicao = this.editandoId !== null
        ? this.api.atualizarConsulta(this.editandoId, payload)
        : this.api.criarConsulta(payload);
    }
    this.salvando = true;

    requisicao
      .pipe(
        finalize(() => {
          this.salvando = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => {
          // Fecha o modal após confirmação da API
          this.modalAberto = false;

          // Limpa os dados da edição/cadastro
          this.editandoId = null;
          this.formulario = this.formVazio();

          // Atualiza a interface imediatamente
          this.cdr.detectChanges();

          // Recarrega a listagem
          this.filtrar();

          void Swal.fire({
            icon: 'success',
            title: 'Lembrete salvo com sucesso!',
            timer: 1600,
            showConfirmButton: false,
            confirmButtonColor: '#064F82'
          });
        },

        error: err => {
          this.erro(err, 'Não foi possível salvar o lembrete.');
          this.cdr.detectChanges();
        }
      });
  }

  async excluir(item: LembreteParceiro): Promise<void> {
    const resposta = await Swal.fire({
      title: 'Excluir lembrete?', text: `${item.titulo} — ${item.nomeUsuario}`,
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar', confirmButtonColor: '#b42318'
    });
    if (!resposta.isConfirmed) return;
    this.api.excluir(item.tipo, item.id).subscribe({
      next: () => {
        this.filtrar();

        void Swal.fire({
          icon: 'success',
          title: 'Lembrete excluído!',
          timer: 1400,
          showConfirmButton: false
        });

        this.cdr.detectChanges();
      },

      error: err => {
        this.erro(err, 'Não foi possível excluir o lembrete.');
        this.cdr.detectChanges();
      }
    });
  }

  private erro(err: any, fallback: string): void {
    const mensagem = err?.error?.detail || err?.error?.message || err?.error?.error || fallback;
    void Swal.fire({ icon: 'error', title: 'Ops!', text: typeof mensagem === 'string' ? mensagem : fallback });
  }
}
